import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiDownload } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { foundationAPI, jobApiError } from '../services/api';
import { Dot, UNIT } from './FoundationStep2';
import './MyAnalytics.css';

// My Analytics (brainstorms/2026-10-23-per-role-analytics.md, Phase 1): the
// person's values KPIs, a section per role they hold (its KPIs and the RACI
// rows it is Accountable or Responsible for), their team tasks and the
// company KPIs. Assessors, the CEO, the Chairman and the Coach pick a person.

const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
export const fmt = (value, unit) => (value == null ? '—' : `${Number(value).toLocaleString()}${UNIT[unit] || ''}`);
const LETTER = { A: 'Accountable', R: 'Responsible', 'A/R': 'Accountable and responsible' };

// Six months, oldest first; a month with no value leaves a gap in the line.
export const Trend = ({ points, unit }) => {
  const values = points.map((p) => (p.value == null ? null : Number(p.value)));
  const known = values.filter((v) => v != null);
  if (known.length < 2) return <div className="ma-muted ma-trend-empty">Not enough months yet for a trend</div>;
  const min = Math.min(...known);
  const span = Math.max(...known) - min || 1;
  const x = (i) => 4 + (i * 112) / (values.length - 1);
  const y = (v) => 26 - ((v - min) / span) * 22;
  const segments = [];
  values.forEach((v, i) => {
    if (v == null) return;
    const last = segments[segments.length - 1];
    if (last && values[i - 1] != null) last.push([x(i), y(v)]);
    else segments.push([[x(i), y(v)]]);
  });
  const label = points.map((p) => `${p.period}: ${p.value == null ? 'no value' : fmt(p.value, unit)}`).join(', ');
  return (
    <svg className="ma-trend" viewBox="0 0 120 30" role="img" aria-label={`Trend: ${label}`}>
      <title>{label}</title>
      {segments.map((seg, i) => (seg.length > 1
        ? <polyline key={i} points={seg.map((p) => p.join(',')).join(' ')} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        : null))}
      {values.map((v, i) => (v == null ? null : <circle key={i} cx={x(i)} cy={y(v)} r={i === values.length - 1 ? 3 : 1.8} fill="currentColor" />))}
    </svg>
  );
};

const KpiTile = ({ kpi }) => (
  <div className="ma-tile" data-colour={kpi.colour || 'GREY'} title={kpi.evidence ? `Evidence: ${kpi.evidence}` : kpi.how_measured}>
    <div className="ma-tile-head">
      <span className="ma-code">{kpi.code}</span>
      <Dot colour={kpi.colour} />
    </div>
    <div className="ma-tile-name">{kpi.name}</div>
    <div className="ma-value">{fmt(kpi.value, kpi.unit)}</div>
    <div className="ma-muted">
      Last month: {fmt(kpi.previous_value, kpi.unit)}
      {kpi.value != null && kpi.previous_value != null && Number(kpi.value) !== Number(kpi.previous_value) && (
        <span aria-hidden="true"> {Number(kpi.value) > Number(kpi.previous_value) ? '↑' : '↓'}</span>
      )}
    </div>
    {kpi.team_value !== undefined && <div className="ma-muted">Team total: {fmt(kpi.team_value, kpi.unit)}</div>}
    <Trend points={kpi.trend || []} unit={kpi.unit} />
    <div className="ma-muted">
      Target {kpi.target_text} · {kpi.frequency}
      {kpi.mode === 'AUTO' ? ' · calculated by the VMS' : ' · entered monthly'}
    </div>
    {kpi.baseline_first && <div className="ma-muted">Baseline quarter: grey until the 9 Jan 2027 review.</div>}
    {kpi.source === 'MANUAL' && kpi.mode === 'AUTO' && <div className="ma-muted">Corrected by hand (see evidence).</div>}
  </div>
);

const Tiles = ({ items }) => (items.length
  ? <div className="ma-tiles">{items.map((k) => <KpiTile key={k.id} kpi={k} />)}</div>
  : <p className="ma-muted">No KPIs for this yet.</p>);

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const RaciRows = ({ rows }) => (
  <table className="ma-raci">
    <thead><tr><th>RACI</th><th>Task</th><th>Your part</th><th>Numbers</th></tr></thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.code}>
          <td className="ma-code">{r.code}</td>
          <td>{r.task}{r.group && <span className="ma-muted"> · {r.group}</span>}</td>
          <td>{LETTER[r.letter] || r.letter}</td>
          <td>
            {r.measures.map((m) => (
              <div key={m.code} className="ma-measure">
                <span className="ma-code">{m.code}</span> {m.value != null ? fmt(m.value, m.unit) : '—'} <Dot colour={m.colour} />
              </div>
            ))}
            {r.indicator && (
              // Phase 4 indicator: a number with no target or colour, not a KPI.
              <div className="ma-measure" title={r.indicator.evidence || 'Indicator: no target, not a KPI'}>
                {r.indicator.name}: <strong>{r.indicator.unit === 'MONEY' ? money(r.indicator.value) : fmt(r.indicator.value, r.indicator.unit)}</strong>
                {r.indicator.previous_value != null && <span className="ma-muted"> (last month {r.indicator.unit === 'MONEY' ? money(r.indicator.previous_value) : fmt(r.indicator.previous_value, r.indicator.unit)})</span>}
                {r.indicator.team_value != null && <span className="ma-muted"> · team {fmt(r.indicator.team_value, r.indicator.unit)}</span>}
              </div>
            )}
            {!r.measures.length && !r.indicator && <span className="ma-muted">Not measured yet</span>}
            {r.link && <Link className="ma-open" to={r.link}>Open</Link>}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

// The Company section (Q4): the old board report, every figure for the chosen month. Counts only.
const CompanyReport = ({ r }) => {
  const print = () => {
    document.body.classList.add('ma-print-company');
    const done = () => { document.body.classList.remove('ma-print-company'); window.removeEventListener('afterprint', done); };
    window.addEventListener('afterprint', done);
    window.print();
  };
  return (
    <section className="ma-section ma-company">
      <div className="ma-company-head">
        <h3>Company — {r.label}</h3>
        <button type="button" className="btn-secondary" onClick={print}><FiDownload /> Download PDF</button>
      </div>
      <div className="ma-stats">
        <div><span className="ma-muted">New client records</span><strong>{r.totals.new_records}</strong></div>
        <div><span className="ma-muted">Signed</span><strong>{r.totals.signed}</strong></div>
        <div><span className="ma-muted">Value signed</span><strong>{money(r.totals.signed_value)}</strong></div>
        <div><span className="ma-muted">Money received (receipts)</span><strong>{money(r.totals.received_usd)}</strong></div>
        <div><span className="ma-muted">NPS ({r.nps.answers} answers)</span><strong>{r.nps.score ?? '—'}</strong></div>
      </div>
      {r.totals.received_note && <p className="ma-muted">{r.totals.received_note}</p>}

      <h4>Sales by service line</h4>
      <div className="ma-scroll">
        <table className="ma-raci">
          <thead><tr><th>Line</th><th>New</th><th>Signed</th><th>Value signed</th><th>All records</th><th>Signed to date</th><th>Conversion</th></tr></thead>
          <tbody>
            {r.service_lines.map((l) => (
              <tr key={l.key} title={`Signed = ${l.signed_rule}`}>
                <td>{l.label}</td><td>{l.new_leads_this_month}</td><td>{l.signed_this_month}</td>
                <td>{l.revenue_this_month == null ? '—' : money(l.revenue_this_month)}</td>
                <td>{l.total_leads}</td><td>{l.signed_clients}</td><td>{l.conversion_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4>Delivery and money KPIs</h4>
      <div className="ma-stats">
        {r.delivery.map((k) => (
          <div key={k.code} title={k.evidence}>
            <span className="ma-muted">{k.code} {k.name}</span>
            <strong>{k.unit === 'MONEY' ? money(k.value) : fmt(k.value, k.unit)}</strong>
            <Dot colour={k.colour} />
          </div>
        ))}
      </div>

      <h4>Finance (from the Finance module)</h4>
      {r.finance.error ? <p className="ma-muted">{r.finance.error}</p> : (
        <>
          <p className="ma-muted">Lifetime value per enrolled student: {money(r.finance.ltv_usd)} · Cash is today's balance.</p>
          {r.finance.entities.length ? (
            <table className="ma-raci">
              <thead><tr><th>Entity</th><th>CAC (fiscal period)</th><th>Cash today</th></tr></thead>
              <tbody>
                {r.finance.entities.map((e) => (
                  <tr key={e.name}><td>{e.name}</td><td>{money(e.cac_usd)}{e.fiscal_period ? ` (${e.fiscal_period})` : ' (no fiscal period)'}</td><td>{money(e.cash_usd)}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <p className="ma-muted">No legal entities set up in Finance.</p>}
        </>
      )}

      <div className="ma-two">
        <div>
          <h4>Active files per staff member (today)</h4>
          {r.workload.length ? (
            <table className="ma-raci"><tbody>{r.workload.map((w) => <tr key={w.name}><td>{w.name}</td><td>{w.active_files}</td></tr>)}</tbody></table>
          ) : <p className="ma-muted">No open files assigned to staff.</p>}
        </div>
        <div>
          <h4>New student records by lead source</h4>
          {r.lead_sources.length ? (
            <table className="ma-raci"><tbody>{r.lead_sources.map((s) => <tr key={s.source}><td>{s.source}</td><td>{s.count}</td></tr>)}</tbody></table>
          ) : <p className="ma-muted">No new records this month.</p>}
        </div>
      </div>
    </section>
  );
};

const MyAnalytics = () => {
  const { isCoach } = useAuth();
  const [period, setPeriod] = useState(thisMonth());
  const [person, setPerson] = useState('');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(() => foundationAPI.myAnalytics(period, person)
    .then((r) => { setData(r.data); setErr(''); })
    .catch((e) => setErr(jobApiError(e))), [period, person]);
  useEffect(() => { load(); }, [load]);

  const coachHome = isCoach() && data?.is_self;
  return (
    <div className="ma-page">
      <div className="ma-header">
        <h2>{!data || data.is_self ? 'My Analytics' : `${data.person.name} — Analytics`}</h2>
        <div className="ma-controls">
          {data?.people?.length > 0 && (
            <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person">
              <option value="">{isCoach() ? 'Choose a person' : 'Me'}</option>
              {data.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Month" />
        </div>
      </div>
      {err && <div className="error-message ma-error"><FiAlertCircle size={16} />{err}</div>}
      {data && (
        <>
          {data.frozen && (
            <p className="ma-notice">This month is closed: its figures are frozen. Only the CEO or Chairman can correct them, with evidence.</p>
          )}
          <p className="ma-muted">
            Green = target met · Amber = up to 20% short · Red = more than 20% short.
            A Red KPI starts a support conversation, never a disciplinary one.
          </p>

          {coachHome ? (
            <p className="ma-notice">Choose a person above to see their analytics.</p>
          ) : (
            <>
              {data.no_role && (
                <p className="ma-notice">
                  {data.is_self ? 'You have' : `${data.person.name} has`} no role in the roles register yet — ask the CEO.
                </p>
              )}

              <section className="ma-section">
                <h3>Values and team rules</h3>
                <Tiles items={data.values} />
              </section>

              {data.roles.map((role) => (
                <section className="ma-section" key={role.code}>
                  <h3>
                    {role.covering ? `Covering: ${role.name}` : role.name}
                    {role.area && <span className="ma-muted"> · {role.area}</span>}
                    {role.covering && <span className="ma-muted"> · until {role.covering.ends_on}</span>}
                  </h3>
                  {(role.covered_by || []).map((c) => (
                    <p className="ma-muted ma-covered-by" key={`${c.name}-${c.starts_on}`}>{c.label}</p>
                  ))}
                  <Tiles items={role.kpis} />
                  {role.raci.length > 0 && (
                    <>
                      <h4>What this role is accountable or responsible for</h4>
                      <RaciRows rows={role.raci} />
                    </>
                  )}
                </section>
              ))}

              <section className="ma-section">
                <h3>Team tasks</h3>
                <p>
                  {data.tasks.open} open · <strong className={data.tasks.overdue ? 'ma-overdue' : ''}>{data.tasks.overdue} overdue</strong>
                  {data.is_self && <> · <Link to="/team-tasks">Team Tasks</Link> · <Link to="/my-duties">My Duties</Link></>}
                </p>
              </section>
            </>
          )}

          <section className="ma-section">
            <h3>Company KPIs</h3>
            <Tiles items={data.company} />
          </section>

          {data.company_report && <CompanyReport r={data.company_report} />}
        </>
      )}
    </div>
  );
};

export default MyAnalytics;
